/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { useMutation } from '@tanstack/react-query';

import { ApiError, BulkOperationalStateChangeNode, BulkOperationalStateChangeResponse } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type BulkOperationalStateRequest = {
  nodes: BulkOperationalStateChangeNode[];
};

export type BulkOperationalStateMutationConfig = {
  onSuccess?: (data: BulkOperationalStateChangeResponse, variables: BulkOperationalStateRequest) => void;
  onError?: (error: unknown, variables: BulkOperationalStateRequest) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
  onMutate?: (variables: BulkOperationalStateRequest) => void;
};

/**
 * Hook for bulk changing operational state of multiple nodes.
 *
 * @param config.onSuccess - Called after successful state change request
 * @param config.onError - Called if the request fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 * @param config.onMutate - Called before the mutation function is fired
 *
 * @returns React Query mutation result for bulk operational state change
 *
 * @example
 * ```tsx
 * const { mutate } = useBulkOperationalStateMutation({
 *   onSuccess: (data) => console.log('State change requested:', data),
 *   onError: (error) => console.error('State change failed:', error)
 * });
 *
 * // Toggle nodes to active
 * mutate({ nodes: [{ nodeId: "node1", requestedOperationalState: "active" }] });
 * ```
 */
export function useBulkOperationalStateMutation({
  onSuccess,
  onError,
  onSettled,
  onMutate,
}: BulkOperationalStateMutationConfig) {
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const { mutate, mutateAsync, isPending, isError, error } = useMutation<
    BulkOperationalStateChangeResponse,
    ApiError,
    BulkOperationalStateRequest
  >({
    mutationFn: (request: BulkOperationalStateRequest) => {
      return mgmtPlaneAPIClient.nodes.changeNodesOperationalState({
        tenantId: tenantId!,
        requestBody: {
          nodes: request.nodes,
        },
      });
    },
    onSuccess,
    onError,
    onSettled,
    onMutate,
  });

  return { mutate, mutateAsync, isPending, isError, error };
}
